/* eslint-disable no-underscore-dangle */
import { ProfilePhotoService } from 'src/app/services/profile-photo.service';
import { UserMediaService } from 'src/app/services/user-media.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { User } from 'src/app/models/user.model';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { SnackService } from 'src/app/services/snack.service';
import { getFileSizeError, getUploadErrorMessage } from 'src/app/shared/utils/file-upload.utils';
import { Patient } from '../../../models/patient.model';

@Component({
  selector: 'app-demographics',
  templateUrl: './demographics.component.html',
  styleUrls: ['./demographics.component.scss'],
})
export class DemographicsComponent implements OnInit, OnDestroy {
  user: User & Patient;
  photo$: Observable<string>;

  title = 'cloudStorage';
  selectedFile: File = null;

  downloadURL: Observable<string>;
  unsubscribe = new Subject<void>();

  isLoading = false;

  constructor(
    private db: AngularFirestore,
    private snackservice: SnackService,
    private auth: NewAuthService,
    private userMediaService: UserMediaService,
    private profilePhotoService: ProfilePhotoService
  ) {}

  ngOnInit() {
    this.auth.currentUser$.pipe(takeUntil(this.unsubscribe)).subscribe((user) => {
      this.user = {
        ...user,
        twilio_line: Array.isArray(user.twilio_line) ? user.twilio_line : [user.twilio_line],
        dob: this.getDateFromDOB(user.dob),
      };
      this.photo$ = this.photoFor(user?.user_id);
    });
  }

  getDateFromDOB(dob: Date | string | any): Date {
    if (dob instanceof Date) {
      return dob;
    } else if (dob instanceof firebase.firestore.Timestamp) {
      return dob.toDate();
    } else if (typeof dob === 'string') {
      return new Date(dob);
    }
  }

  onFileSelected(event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    // Clear the input so picking the same file again after a rejection still fires a change event.
    input.value = '';

    const sizeError = getFileSizeError(file);
    if (sizeError) {
      this.snackservice.genericSnackBar(sizeError, ['error-snackbar'], 6000);
      return;
    }

    this.isLoading = true;
    this.selectedFile = file;
    this.userMediaService
      .uploadProfilePhoto(file)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => this.reloadUser(),
        error: (error) => {
          this.isLoading = false;
          console.error('Error uploading file', error);
          this.snackservice.genericSnackBar(getUploadErrorMessage(error), ['error-snackbar'], 6000);
        },
      });
  }

  /** A presigned link to the user's photo, or the default avatar. */
  photoFor(userId: string): Observable<string> {
    return this.profilePhotoService.url$(userId).pipe(map((url) => url ?? 'assets/images/default-profile.png'));
  }

  /** The API already saved the photo; drop the cached link and reload the user to show it. */
  async reloadUser() {
    try {
      this.profilePhotoService.invalidate(this.user.user_id);
      this.photo$ = this.photoFor(this.user.user_id);
      this.user = (await this.db.collection('users').doc(this.user.user_id).get().pipe(take(1)).toPromise()).data() as Patient;
      this.user.dob = this.getDateFromDOB(this.user.dob);
    } catch (error) {
      // Without this the spinner ran forever on a failed write — the very symptom being fixed here.
      this.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  drop(event: CdkDragDrop<string[]>): void {
    if (typeof this.user.twilio_line === 'string') {
      this.user.twilio_line = [this.user.twilio_line];
    }
    moveItemInArray(this.user.twilio_line, event.previousIndex, event.currentIndex);
    this.saveIndexesChanges();
    if (event.currentIndex === 0) {
      const twilioNumber = this.user.twilio_line[0];
      this.snackservice.genericSnackBar(`The ${twilioNumber} has been updated as the default Twilio number`, ['success-snackbar'], 6000);
    }
  }

  async saveIndexesChanges(): Promise<void> {
    await this.auth.saveIndexDragAndDrop(this.auth.user.user_id, this.user.twilio_line);
  }

  // Default error handling for all actions
  private handleError(error) {
    console.log(error);
    this.snackservice.genericSnackBar(error?.message ?? 'Internal error', ['error-snackbar'], 6000);
  }
}
